import React from "react";
/* Sidebar — Phase B primitive.

   Props:
     items:       Array<{ id, label, icon, href? }>
     currentId:   string — which item is active
     onNavigate:  (id) => void
     collapsed:   bool — icon-only mode
     onToggleCollapsed: () => void — header chevron handler
     brand:       ReactNode — top-of-sidebar brand block

   Icon library: Lucide-style line icons baked as inline SVG glyphs.
   The `icon` prop is a ReactNode (typically a <Svg> from icon set, but a string
   like "◆" works for the Phase B showcase). */

export default function Sidebar({
  items = [],
  currentId,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  brand,
  className = "",
  ...rest
}) {
  const cls = [
    "v2-sidebar",
    collapsed ? "v2-sidebar--collapsed" : "v2-sidebar--expanded",
    className,
  ].filter(Boolean).join(" ");

  return (
    <nav className={cls} role="navigation" aria-label="Primary" {...rest}>
      <div className="v2-sidebar__header">
        <button
          type="button"
          className="v2-sidebar__toggle"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
        >
          {/* Standard hamburger glyph — always shown, click toggles collapse.
              Three SVG lines for proper rendering at any pixel density. */}
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="2.5" y1="4" x2="13.5" y2="4" />
            <line x1="2.5" y1="8" x2="13.5" y2="8" />
            <line x1="2.5" y1="12" x2="13.5" y2="12" />
          </svg>
        </button>
        {!collapsed && brand ? <div className="v2-sidebar__brand">{brand}</div> : null}
      </div>
      <ul className="v2-sidebar__list" role="list">
        {items.map(item => {
          const active = item.id === currentId;
          return (
            <li key={item.id} className="v2-sidebar__list-item">
              <button
                type="button"
                className={`v2-sidebar__item ${active ? "v2-sidebar__item--active" : ""}`.trim()}
                aria-current={active ? "page" : undefined}
                onClick={() => onNavigate?.(item.id)}
                title={collapsed ? item.label : undefined}
              >
                {item.icon != null ? <span className="v2-sidebar__icon" aria-hidden="true">{item.icon}</span> : null}
                {!collapsed ? <span className="v2-sidebar__label">{item.label}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
